import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import Alert from '@cloudscape-design/components/alert';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import TokenGroup from '@cloudscape-design/components/token-group';
import type {
  ConfirmStatementEntry,
  FinanceAccount,
  ParseBankStatementImageOutput,
} from '@etiquetachef/types';
import { FinanceImportId, FinanceAccountId } from '@etiquetachef/types';
import { storage } from '@/lib/firebase';
import { getFinanceImport, subscribeFinanceAccounts } from '@/lib/firestoreFinance';
import {
  parseBankStatementImageCallable,
  confirmStatementImportCallable,
  listFinanceTagsCallable,
} from '@/lib/callables';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { toast } from '@/lib/notifications';
import { assertCanWrite } from '@etiquetachef/ui-shared/online';

const MIME_EXT: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

type Row = {
  /** Chave estável da linha (índice no preview) — extratos têm datas repetidas. */
  key: string;
  include: boolean;
  type: 'IN' | 'OUT';
  /** Valor decimal editável (ex.: "12.99"). */
  amount: string;
  date: number;
  description: string;
  tags: string[];
  /** Texto em digitação no Autosuggest de tags desta linha. */
  tagInput: string;
  isDuplicate: boolean;
};

const LLM_ERROR_REASONS = ['LLM_BILLING', 'LLM_AUTH', 'LLM_RATE_LIMIT', 'LLM_OVERLOADED'] as const;

/**
 * Chave i18n do erro de parse (#104): o callable manda `details.reason` quando
 * a falha é da Anthropic API (sem créditos, key inválida, rate limit,
 * overload); sem reason reconhecido cai no genérico `parseFailed`.
 */
function parseErrorKey(err: unknown): string {
  const reason = (err as { details?: { reason?: unknown } } | null)?.details?.reason;
  if (typeof reason === 'string' && (LLM_ERROR_REASONS as readonly string[]).includes(reason)) {
    return `admin.finance.imports.errors.${reason}`;
  }
  return 'admin.finance.imports.errors.parseFailed';
}

type ReopenOutcome =
  | { kind: 'ready'; accountId: string; imagePath: string; preview: ParseBankStatementImageOutput }
  | { kind: 'error'; messageKey: string };

/** Carrega o DRAFT e re-roda o parse sobre a imagem já no Storage. */
async function loadReopenPreview(importId: string): Promise<ReopenOutcome> {
  const imp = await getFinanceImport(importId);
  if (imp === null) {
    return { kind: 'error', messageKey: 'admin.finance.imports.errors.notFound' };
  }
  if (imp.status === 'CONFIRMED') {
    return { kind: 'error', messageKey: 'admin.finance.imports.errors.alreadyConfirmed' };
  }
  const preview = await parseBankStatementImageCallable({
    importId: FinanceImportId(imp.id),
    accountId: FinanceAccountId(imp.accountId),
    imagePath: imp.imagePath,
  });
  return { kind: 'ready', accountId: imp.accountId, imagePath: imp.imagePath, preview };
}

/** Dois dígitos com zero à esquerda. */
function pad(n: number): string {
  return String(n).padStart(2, '0');
}
/** `DD/MM/AAAA` em UTC — a data do extrato é gravada ao meio-dia UTC. */
function formatDateMs(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${String(d.getUTCFullYear())}`;
}
/** Centavos → texto decimal editável (`1299` → `"12.99"`). */
function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}
/** Texto decimal (vírgula ou ponto) → centavos; `null` se inválido ou ≤ 0. */
function inputToCents(value: string): number | null {
  const n = Number(value.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

/**
 * Tela de import de extrato bancário: envia a imagem, o LLM extrai as
 * linhas, o admin revisa (tipo, valor, data, descrição, tags) e confirma
 * os lançamentos.
 *
 * Origem: #75 — 2026-06-21 22:25 +0200 (import de extrato bancário por
 * OCR/LLM com preview + dedupe).
 *
 * Por quê: lançar extrato à mão é lento e erra; o preview editável deixa o
 * humano decidir antes de gravar, e o dedupe por fingerprint impede lançar
 * duas vezes a mesma linha de extratos sobrepostos.
 *
 * Contrato: `/finance/imports/new` — escolhe conta (não arquivada) e
 * imagem (png/jpeg/webp/gif); "Analisar" sobe para o Storage em
 * `finance_imports/<uuid>/statement.<ext>` e chama `parseBankStatementImage`
 * (cria o DRAFT, devolve o preview). `/finance/imports/:importId` —
 * `loadReopenPreview` relê o DRAFT e roda o parse de novo sobre a mesma
 * imagem; `CONFIRMED` ou inexistente vira erro. "Confirmar" manda só as
 * linhas incluídas, não duplicadas, com valor > 0 e descrição, para
 * `confirmStatementImport` (devolve `created`/`skipped`) e volta para
 * `/finance/imports`. As duas callables exigem a capability
 * `manageFinance` (ROOT/FINANCE, `docs/permissions.md`);
 * `listFinanceTags` (leitura) sugere tags. Falha da Anthropic API chega
 * com `details.reason` e vira mensagem específica (`parseErrorKey`, #104).
 *
 * Usado por: `routes/router.tsx` (rotas `finance/imports/new` e
 * `finance/imports/:importId`, dentro do `RequireSuperAdmin`); entra pelos
 * botões "Importar extrato" e "Reabrir" da `FinanceImportsPage` (menu
 * Financeiro → "Importações"). Só `apps/admin` (web). Spec:
 * `docs/screens/finance-imports-admin.md`.
 *
 * Arquitetura: o cliente só escreve no Storage (regra
 * `match /finance_imports/{importId}/{fileName}`: super-admin, < 10 MB,
 * imagem raster); `finance_imports` e `finance_entries` são escritos só
 * pelas callables (Admin SDK). Duplicadas nunca entram pela UI (pedido do
 * owner 2026-07-02); o servidor também as pularia pelo fingerprint.
 *
 * Dados: preview `ParseBankStatementImageOutput`
 * (`packages/types/src/finance.ts`): `lines[]` com `type` IN | OUT,
 * `amountCents` (CENTAVOS — a linha edita em decimal "12.99" e volta com
 * `inputToCents`), `date` em ms epoch ao meio-dia UTC (exibida em UTC),
 * `isNew`/`isDuplicate`. Confirmação: `{ importId, entries:
 * [{ type, amountCents, date, description, tags }] }`.
 *
 * Falhas conhecidas:
 *  - 2026-07-02 (#104): falha da Anthropic API (sem crédito, key, rate
 *    limit, overload) aparecia como "falha no parse" genérica.
 *
 * Dicas: cada reabertura de DRAFT roda o LLM de novo (custo por abertura).
 * Se o parse falhar depois do upload, a imagem fica no Storage sem sessão
 * útil. Tags por linha: ≤ 20, aceita "a, b, c" colado.
 *
 * Issues: #75 (origem), #77 (tags), #104 (erros do LLM).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function FinanceImportFlowPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { importId: reopenId } = useParams<{ importId: string }>();

  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [accountId, setAccountId] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ParseBankStatementImageOutput | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [imagePath, setImagePath] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [knownTags, setKnownTags] = useState<string[]>([]);

  useEffect(() => {
    const unsubA = subscribeFinanceAccounts((r) => {
      if (r.kind === 'READY') setAccounts(r.accounts);
    });
    // Tags já usadas nos lançamentos — sugeridas ao clicar na caixa de tags
    // de cada linha (padroniza a digitação para a Análise por tag, #77).
    listFinanceTagsCallable({})
      .then((r) => {
        setKnownTags(r.tags);
      })
      .catch((err: unknown) => {
        console.error('listFinanceTags failed', err);
      });
    return (): void => {
      unsubA();
    };
  }, []);

  /** Abre a imagem do extrato (URL de download em cache após a 1ª vez). */
  async function openImage(): Promise<void> {
    if (imagePath === null) return;
    try {
      const url = imageUrl ?? (await getDownloadURL(storageRef(storage, imagePath)));
      setImageUrl(url);
      setImageOpen(true);
    } catch (err) {
      console.error('getDownloadURL failed', err);
      // Auditoria 2026-10-25 14:29 +0100 (#1021): a falha só ia para o console e para o Alert do topo da
      // página. Agora vai para a telemetria e também em toast (Flashbar fixo no topo).
      reportNonFatal('admin_finance_import_flow_image_failed', {
        code: (err as { code?: string }).code ?? null,
        ...serializeError(err),
      });
      setError(t('admin.finance.imports.errors.imageLoadFailed'));
      toast.error(t('admin.finance.imports.errors.imageLoadFailed'));
    }
  }

  const applyPreview = useCallback((result: ParseBankStatementImageOutput): void => {
    setPreview(result);
    setRows(
      result.lines.map((l, idx) => ({
        key: String(idx),
        include: l.isNew,
        type: l.type,
        amount: centsToInput(l.amountCents),
        date: l.date,
        description: l.description,
        tags: [],
        tagInput: '',
        isDuplicate: l.isDuplicate,
      })),
    );
  }, []);

  // Reabrir uma sessão DRAFT: re-roda o parse sobre a imagem já no Storage.
  // Padrão `.then/.catch` (não IIFE async) p/ casar com `useFinanceEntriesSearch`.
  useEffect(() => {
    if (reopenId === undefined) return;
    let cancelled = false;
    setBusy(true);
    loadReopenPreview(reopenId)
      .then((outcome) => {
        if (cancelled) return;
        if (outcome.kind === 'error') {
          setError(t(outcome.messageKey));
        } else {
          setAccountId(outcome.accountId);
          setImagePath(outcome.imagePath);
          applyPreview(outcome.preview);
        }
        setBusy(false);
      })
      .catch((err: unknown) => {
        console.error('reopen import failed', err);
        if (!cancelled) {
          setError(t(parseErrorKey(err)));
          setBusy(false);
        }
      });
    return (): void => {
      cancelled = true;
    };
  }, [reopenId, applyPreview, t]);

  const accountOptions: SelectProps.Option[] = useMemo(
    () => accounts.filter((a) => !a.archived).map((a) => ({ value: a.id, label: a.name })),
    [accounts],
  );
  /** Sobe a imagem e roda `parseBankStatementImage`; erro vira mensagem (`parseErrorKey`). */
  async function onAnalyze(): Promise<void> {
    const file = files[0];
    if (accountId === '' || file === undefined) return;
    const ext = MIME_EXT[file.type];
    if (ext === undefined) {
      setError(t('admin.finance.imports.errors.badFormat'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      assertCanWrite();
      const importId = crypto.randomUUID();
      const path = `finance_imports/${importId}/statement.${ext}`;
      await uploadBytes(storageRef(storage, path), file, { contentType: file.type });
      const result = await parseBankStatementImageCallable({
        importId: FinanceImportId(importId),
        accountId: FinanceAccountId(accountId),
        imagePath: path,
      });
      setImagePath(path);
      applyPreview(result);
    } catch (err) {
      console.error('parseBankStatementImage failed', err);
      setError(t(parseErrorKey(err)));
    } finally {
      setBusy(false);
    }
  }

  /** Aplica um patch parcial à linha `index` do preview. */
  function patchRow(index: number, patch: Partial<Row>): void {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  /** Adiciona tag(s) à linha — aceita "a, b, c" colado; dedupe; ≤ 20. */
  function addRowTag(index: number, override?: string): void {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const raw = (override ?? r.tagInput).trim();
        if (raw === '') return r;
        const next = [...r.tags];
        for (const tg of raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0)) {
          if (!next.includes(tg) && next.length < 20) next.push(tg);
        }
        return { ...r, tags: next, tagInput: '' };
      }),
    );
  }

  // Duplicadas nunca entram (o servidor já pularia pelo fingerprint; a UI
  // não deixa nem tentar — pedido do owner 2026-07-02).
  const includedValid = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.include &&
          !r.isDuplicate &&
          inputToCents(r.amount) !== null &&
          r.description.trim() !== '',
      ),
    [rows],
  );

  /** Grava as linhas válidas por `confirmStatementImport` e volta ao histórico. */
  async function onConfirm(): Promise<void> {
    if (preview === null || includedValid.length === 0) return;
    const entries: ConfirmStatementEntry[] = includedValid.map((r) => {
      const cents = inputToCents(r.amount);
      return {
        type: r.type,
        amountCents: cents ?? 0,
        date: r.date,
        description: r.description.trim(),
        tags: r.tags,
      };
    });
    setBusy(true);
    setError(null);
    try {
      const out = await confirmStatementImportCallable({
        importId: FinanceImportId(preview.importId),
        entries,
      });
      toast.success(
        t('admin.finance.imports.success.confirmed', { count: out.created, skipped: out.skipped }),
      );
      void navigate('/finance/imports');
    } catch (err) {
      console.error('confirmStatementImport failed', err);
      setError(t('admin.finance.imports.errors.confirmFailed'));
      // Auditoria 2026-10-25 14:29 +0100 (#1021): o Confirmar fica abaixo da prévia longa e o Alert no
      // topo; sem o toast quem clicou não via a falha. A callable já reporta pelo wrapper (`callable`).
      toast.error(t('admin.finance.imports.errors.confirmFailed'));
      setBusy(false);
    }
  }

  const typeSegments = [
    { id: 'IN', text: t('admin.finance.entries.type.IN') },
    { id: 'OUT', text: t('admin.finance.entries.type.OUT') },
  ];

  const showPicker = preview === null && reopenId === undefined;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('admin.finance.imports.flowSubtitle')}
          actions={
            <Button
              iconName="angle-left"
              onClick={(): void => {
                void navigate('/finance/imports');
              }}
            >
              {t('common.back')}
            </Button>
          }
        >
          {t('admin.finance.imports.flowTitle')}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error !== null && <Alert type="error">{error}</Alert>}

        {showPicker && (
          <Container header={<Header variant="h2">{t('admin.finance.imports.pickTitle')}</Header>}>
            <SpaceBetween size="m">
              <FormField label={t('admin.finance.imports.account')}>
                <Select
                  selectedOption={accountOptions.find((o) => o.value === accountId) ?? null}
                  options={accountOptions}
                  placeholder={t('admin.finance.imports.accountPlaceholder')}
                  onChange={({ detail }): void => {
                    setAccountId(detail.selectedOption.value ?? '');
                  }}
                />
              </FormField>
              <FormField
                label={t('admin.finance.imports.image')}
                description={t('admin.finance.imports.imageHint')}
              >
                <FileUpload
                  value={files}
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={({ detail }): void => {
                    setFiles(detail.value);
                  }}
                  i18nStrings={{
                    uploadButtonText: () => t('admin.finance.imports.chooseFile'),
                    dropzoneText: () => t('admin.finance.imports.dropzone'),
                    removeFileAriaLabel: (i) => `${t('admin.finance.actions.delete')} ${String(i + 1)}`,
                    limitShowFewer: '',
                    limitShowMore: '',
                    errorIconAriaLabel: 'Error',
                  }}
                  constraintText={t('admin.finance.imports.imageConstraint')}
                  showFileSize
                />
              </FormField>
              <Box>
                <Button
                  variant="primary"
                  loading={busy}
                  disabled={accountId === '' || files.length === 0}
                  onClick={(): void => {
                    void onAnalyze();
                  }}
                >
                  {t('admin.finance.imports.analyze')}
                </Button>
              </Box>
            </SpaceBetween>
          </Container>
        )}

        {busy && preview === null && reopenId !== undefined && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" /> {t('admin.finance.imports.parsing')}
          </Box>
        )}

        {preview !== null && (
          <SpaceBetween size="m">
            <ColumnLayout columns={3} variant="text-grid">
              <Box>
                <Box variant="awsui-key-label">{t('admin.finance.imports.columns.parsed')}</Box>
                <Box>{preview.parsedCount}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">{t('admin.finance.imports.columns.new')}</Box>
                <Box color="text-status-success">{preview.newCount}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">{t('admin.finance.imports.columns.dup')}</Box>
                <Box color="text-status-inactive">{preview.dupCount}</Box>
              </Box>
            </ColumnLayout>

            <Table<Row>
              items={rows}
              trackBy="key"
              variant="container"
              resizableColumns
              header={
                <Header
                  variant="h2"
                  actions={
                    imagePath !== null && (
                      <Button
                        iconName="external"
                        onClick={(): void => {
                          void openImage();
                        }}
                      >
                        {t('admin.finance.imports.viewImage')}
                      </Button>
                    )
                  }
                >
                  {t('admin.finance.imports.preview.title')}
                </Header>
              }
              empty={
                <Box textAlign="center" color="text-status-inactive" padding="m">
                  {t('admin.finance.imports.preview.empty')}
                </Box>
              }
              columnDefinitions={[
                {
                  id: 'include',
                  header: t('admin.finance.imports.preview.include'),
                  width: 120,
                  cell: (r): React.ReactNode => {
                    const idx = rows.indexOf(r);
                    return (
                      <SpaceBetween size="xxs" direction="horizontal">
                        <Checkbox
                          checked={r.include && !r.isDuplicate}
                          disabled={r.isDuplicate}
                          onChange={({ detail }): void => {
                            patchRow(idx, { include: detail.checked });
                          }}
                        />
                        {r.isDuplicate && (
                          <Badge color="grey">{t('admin.finance.imports.preview.dupBadge')}</Badge>
                        )}
                      </SpaceBetween>
                    );
                  },
                },
                {
                  id: 'date',
                  header: t('admin.finance.entries.columns.date'),
                  width: 120,
                  cell: (r): React.ReactNode => formatDateMs(r.date),
                },
                {
                  id: 'type',
                  header: t('admin.finance.entries.form.type'),
                  width: 210,
                  cell: (r): React.ReactNode => {
                    const idx = rows.indexOf(r);
                    return (
                      <SegmentedControl
                        selectedId={r.type}
                        options={typeSegments}
                        label={t('admin.finance.entries.form.type')}
                        onChange={({ detail }): void => {
                          patchRow(idx, { type: detail.selectedId as 'IN' | 'OUT' });
                        }}
                      />
                    );
                  },
                },
                {
                  id: 'amount',
                  header: t('admin.finance.entries.columns.amount'),
                  width: 140,
                  cell: (r): React.ReactNode => {
                    const idx = rows.indexOf(r);
                    return (
                      <Input
                        type="number"
                        value={r.amount}
                        invalid={inputToCents(r.amount) === null}
                        onChange={({ detail }): void => {
                          patchRow(idx, { amount: detail.value });
                        }}
                      />
                    );
                  },
                },
                {
                  id: 'description',
                  header: t('admin.finance.entries.columns.description'),
                  cell: (r): React.ReactNode => {
                    const idx = rows.indexOf(r);
                    return (
                      <Input
                        value={r.description}
                        invalid={r.description.trim() === ''}
                        onChange={({ detail }): void => {
                          patchRow(idx, { description: detail.value });
                        }}
                      />
                    );
                  },
                },
                {
                  id: 'tags',
                  header: t('admin.finance.entries.columns.tags'),
                  minWidth: 220,
                  cell: (r): React.ReactNode => {
                    const idx = rows.indexOf(r);
                    return (
                      <SpaceBetween size="xxs">
                        <Autosuggest
                          value={r.tagInput}
                          expandToViewport
                          options={knownTags
                            .filter((tg) => !r.tags.includes(tg))
                            .map((tg) => ({ value: tg }))}
                          enteredTextLabel={(v): string => v}
                          placeholder={t('admin.finance.entries.form.tagsPlaceholder')}
                          empty={t('admin.finance.entries.form.tagsNoMatch')}
                          onChange={({ detail }): void => {
                            patchRow(idx, { tagInput: detail.value });
                          }}
                          onSelect={({ detail }): void => {
                            addRowTag(idx, detail.value);
                          }}
                          onKeyDown={({ detail }): void => {
                            if (detail.key === 'Enter') addRowTag(idx);
                          }}
                        />
                        {r.tags.length > 0 && (
                          <TokenGroup
                            items={r.tags.map((tg) => ({ label: tg }))}
                            onDismiss={({ detail }): void => {
                              patchRow(idx, {
                                tags: r.tags.filter((_, i) => i !== detail.itemIndex),
                              });
                            }}
                          />
                        )}
                      </SpaceBetween>
                    );
                  },
                },
              ]}
            />

            <Box float="right">
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  onClick={(): void => {
                    void navigate('/finance/imports');
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  loading={busy}
                  disabled={includedValid.length === 0}
                  onClick={(): void => {
                    void onConfirm();
                  }}
                >
                  {t('admin.finance.imports.confirm', { count: includedValid.length })}
                </Button>
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        )}

        <Modal
          visible={imageOpen}
          onDismiss={(): void => {
            setImageOpen(false);
          }}
          header={t('admin.finance.imports.image')}
          size="medium"
        >
          {imageUrl !== null && (
            <img
              src={imageUrl}
              alt={t('admin.finance.imports.image')}
              style={{
                display: 'block',
                margin: '0 auto',
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          )}
        </Modal>
      </SpaceBetween>
    </ContentLayout>
  );
}
